import React, { useState, useRef, useEffect } from 'react';
import { UploadIcon, XIcon, TrashDeleteIcon, SmallFileIcon } from 'assests/GmsIcons';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import gmsConfig from 'Settings/AppSettings';
import AuthManager from 'Common/AuthManager';
import { createExternalModel, initiateExternalUpload, getUploadPartUrls, completeExternalUpload, abortExternalUpload } from '../ARSServices/ARSServices';
import { checkJobStatus } from 'api/Job';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Radio from 'Components/DesignSystem/Radio/Radio';
import Button from 'Components/DesignSystem/Button/Button';

function pollValidationJobInBackground(jobId: string, onRefresh: () => void): void {
    checkJobStatus(jobId)
        .then(onRefresh)
        .catch(onRefresh);
}

type ModelType = "Co-seller" | "Substitution";

type Props = {
    id: string;
    onClose: () => void;
    onUploadSuccess: () => void;
    channelId: string;
    industryId: string;
};

const ABORT_UPLOAD_PATH = (modelId: string, uploadUuid: string) =>
    `recommender/model/external/${modelId}/upload/${uploadUuid}/abort`;

const ACCEPTED_EXTENSIONS = ['.jsonl'];
const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB — S3 minimum part size
const MAX_CONCURRENT_PARTS = 3;
const MAX_PART_RETRIES = 3;
const PART_URL_BATCH_SIZE = 100;
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB

const isValidFileType = (file: File): boolean =>
    ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));

const formatBytes = (bytes: number): string => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
};

const uploadPartToS3 = (
    uploadUrl: string,
    chunk: Blob,
    onChunkProgress: (loaded: number) => void
): Promise<string> =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) onChunkProgress(e.loaded); };
        xhr.onload = () => {
            if (xhr.status === 200) {
                resolve(xhr.getResponseHeader('ETag') ?? '');
            } else {
                reject(new Error(`Part upload failed: HTTP ${xhr.status}`));
            }
        };
        xhr.onerror = () => reject(new Error('Part upload network error'));
        xhr.send(chunk);
    });

const uploadPartWithRetry = async (
    uploadUrl: string,
    chunk: Blob,
    partNumber: number,
    onChunkProgress: (loaded: number) => void
): Promise<{ part_number: number; etag: string }> => {
    let lastError: Error | null = null;
    for (let attempt = 1; attempt <= MAX_PART_RETRIES; attempt++) {
        try {
            if (attempt > 1) onChunkProgress(0); // Reset stale progress from the failed attempt
            const etag = await uploadPartToS3(uploadUrl, chunk, onChunkProgress);
            return { part_number: partNumber, etag };
        } catch (err) {
            lastError = err as Error;
            if (attempt < MAX_PART_RETRIES) await new Promise(r => setTimeout(r, 1000 * attempt));
        }
    }
    throw lastError;
};

const UploadExternalModelDialog = ({ id, onClose, onUploadSuccess, channelId, industryId }: Props) => {
    const [file, setFile] = useState<File | null>(null);
    const [fileTypeError, setFileTypeError] = useState<string | null>(null);
    const [modelName, setModelName] = useState('');
    const [modelType, setModelType] = useState<ModelType>("Co-seller");
    const [isDragOver, setIsDragOver] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadPhase, setUploadPhase] = useState<'idle' | 'creating' | 'uploading' | 'completing'>('idle');

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // Track active upload session so we can abort on cancel or error
    const activeUploadRef = useRef<{ modelId: string; uploadUuid: string } | null>(null);
    useEffect(() => {
        // Browser refresh/close: keepalive ensures the request is sent even as the page tears down
        const handleBeforeUnload = () => {
            if (!activeUploadRef.current) return;
            const { modelId, uploadUuid } = activeUploadRef.current;
            const token = AuthManager.getGmsToken();
            if (!token) return;
            fetch(
                `${gmsConfig.GMS_BACKEND_API_URL}${ABORT_UPLOAD_PATH(modelId, uploadUuid)}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    keepalive: true,
                    body: JSON.stringify({})
                }
            ).catch(() => null);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // In-app navigation (no page refresh): abort via the regular async call
            if (activeUploadRef.current) {
                const { modelId, uploadUuid } = activeUploadRef.current;
                abortExternalUpload(modelId, uploadUuid).catch(() => null);
                activeUploadRef.current = null;
            }
        };
    }, []);

    const handleFileSelect = (selectedFile: File) => {
        if (!isValidFileType(selectedFile)) {
            setFileTypeError('Invalid file type. Only .jsonl files are accepted.');
            setFile(null);
            return;
        }
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            setFileTypeError(`File exceeds the maximum allowed size of ${formatBytes(MAX_FILE_SIZE_BYTES)}.`);
            setFile(null);
            return;
        }
        setFileTypeError(null);
        setFile(selectedFile);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) handleFileSelect(selectedFile);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) handleFileSelect(droppedFile);
    };

    const isFormValid = file !== null && modelName.trim() !== '';

    const handleCancel = async () => {
        if (activeUploadRef.current) {
            const { modelId, uploadUuid } = activeUploadRef.current;
            abortExternalUpload(modelId, uploadUuid).catch(() => null);
            activeUploadRef.current = null;
        }
        onClose();
    };

    const handleCreate = async () => {
        if (!isFormValid || !file) return;
        setIsCreating(true);

        try {
            // Step 1: Create model record
            setUploadPhase('creating');
            const res = await createExternalModel(modelName.trim(), modelType, channelId, industryId);
            if (!res?.done) {
                toast.error(res?.message ?? 'Failed to create model. Please try again.');
                return;
            }
            const modelId: string = res.model_id;

            // Step 2: Initiate upload — backend derives channel_id from the model, returns first batch of URLs
            const initRes = await initiateExternalUpload(modelId, file.name, file.size);
            if (!initRes?.done || !initRes?.upload_uuid) {
                toast.error(initRes?.message ?? 'Failed to initiate upload. Please try again.');
                return;
            }
            const { upload_uuid: uploadUuid, total_parts: totalParts, parts: firstBatchParts } = initRes;
            activeUploadRef.current = { modelId, uploadUuid };

            // Step 3: Upload all parts directly to S3, batching URL requests every PART_URL_BATCH_SIZE parts
            setUploadPhase('uploading');
            const partProgress: number[] = new Array(totalParts).fill(0);
            const onPartProgress = (partIndex: number, loaded: number) => {
                partProgress[partIndex] = loaded;
                const totalUploaded = partProgress.reduce((a, b) => a + b, 0);
                setUploadProgress(Math.round((totalUploaded / file.size) * 100));
            };

            // Build a URL map from the parts we already have
            const urlMap: Record<number, string> = {};
            for (const p of firstBatchParts) urlMap[p.part_number] = p.upload_url;

            // Process parts in concurrent groups of MAX_CONCURRENT_PARTS
            for (let batchStart = 0; batchStart < totalParts; batchStart += MAX_CONCURRENT_PARTS) {
                const batchEnd = Math.min(batchStart + MAX_CONCURRENT_PARTS, totalParts);
                const batchPromises = [];

                for (let i = batchStart; i < batchEnd; i++) {
                    const partNumber = i + 1; // S3 part numbers are 1-based

                    // If we don't have a URL for this part yet, fetch the next batch
                    if (!urlMap[partNumber]) {
                        const fetchStart = partNumber;
                        const fetchEnd = Math.min(partNumber + PART_URL_BATCH_SIZE - 1, totalParts);
                        const partsRes = await getUploadPartUrls(modelId, uploadUuid, fetchStart, fetchEnd);
                        if (!partsRes?.done) {
                            throw new Error(partsRes?.message ?? 'Failed to fetch upload URLs.');
                        }
                        for (const p of partsRes.parts) urlMap[p.part_number] = p.upload_url;
                    }

                    const start = i * CHUNK_SIZE;
                    const chunk = file.slice(start, start + CHUNK_SIZE);
                    const partIndex = i;
                    batchPromises.push(
                        uploadPartWithRetry(urlMap[partNumber], chunk, partNumber, (loaded) => onPartProgress(partIndex, loaded))
                    );
                }

                await Promise.all(batchPromises);
            }

            // Step 4: Notify backend to complete the multipart upload and queue validation
            setUploadPhase('completing');
            const completeRes = await completeExternalUpload(modelId, uploadUuid);

            if (completeRes?.done) {
                activeUploadRef.current = null;
                const jobId = completeRes?.job_id;
                toast.success('File uploaded successfully. Validation started.');
                setIsCreating(false);
                setUploadProgress(0);
                setUploadPhase('idle');
                onUploadSuccess();
                onClose();
                if (jobId) pollValidationJobInBackground(jobId, onUploadSuccess);
                return;
            } else {
                // S3 commit failed — abort to free any orphaned parts the server didn't clean up
                abortExternalUpload(modelId, uploadUuid).catch(() => null);
                activeUploadRef.current = null;
                toast.error(completeRes?.message ?? 'Upload completion failed. Please try again.');
            }
        } catch (error) {
            console.error('Upload error:', error);
            // Abort orphaned S3 parts on any error
            if (activeUploadRef.current) {
                const { modelId, uploadUuid } = activeUploadRef.current;
                abortExternalUpload(modelId, uploadUuid).catch(() => null);
                activeUploadRef.current = null;
                }
            toast.error('Upload failed. Please check your connection and try again.');
        } finally {
            setIsCreating(false);
            setUploadProgress(0);
            setUploadPhase('idle');
        }
    };

    const phaseLabel: Record<typeof uploadPhase, string> = {
        idle: 'Create',
        creating: 'Creating model...',
        uploading: `Uploading... ${uploadProgress}%`,
        completing: 'Finalising...',
    };

    return (
        <div id={id} className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-800 tw-bg-opacity-50 tw-z-50">
            <div id={`${id}-content`} className="tw-bg-white tw-rounded tw-shadow-md tw-w-[480px]">
                {/* Header */}
                <div id={`${id}-header`} className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-3">
                    <h2 id={`${id}-title`} className="tw-text-sm tw-font-semibold tw-text-[#172B4D]">Upload External Model</h2>
                    <button aria-label="Close" data-testid="components-uploadexternalmodeldialog-button-1" id={`${id}-close-btn`} onClick={handleCancel} disabled={isCreating}><XIcon /></button>
                </div>
                <div className="tw-border-t tw-border-[#D1D1D6]" />

                <div className="tw-px-6 tw-py-4 tw-flex tw-flex-col tw-gap-4">
                    {/* File Upload Area */}
                    <div>
                        {file ? (
                            <div className="tw-flex tw-items-center tw-gap-2 tw-border tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2">
                                <SmallFileIcon />
                                <span className="tw-flex-1 tw-text-sm tw-text-[#172B4D] tw-truncate">{file.name}</span>
                                <span className="tw-text-xs tw-text-[#6B778C] tw-shrink-0">{formatBytes(file.size)}</span>
                                <button aria-label="Delete" data-testid="components-uploadexternalmodeldialog-button-2"
                                    type="button"
                                    disabled={isCreating}
                                    onClick={() => { setFile(null); setFileTypeError(null); }}
                                    className="tw-flex-shrink-0 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer disabled:tw-opacity-40"
                                >
                                    <TrashDeleteIcon />
                                </button>
                            </div>
                        ) : (
                            <div data-testid="components-uploadexternalmodeldialog-div-1"
                                id={`${id}-dropzone`}
                                role="button"
                                tabIndex={0}
                                className={`tw-border-2 tw-border-dashed tw-rounded tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10 tw-px-4 tw-cursor-pointer tw-gap-2 tw-transition-colors ${isDragOver ? 'tw-border-accent tw-bg-blue-100' : 'tw-border-[#D1D1D6]'}`}
                                onClick={() => fileInputRef.current?.click()}
                                onDrop={handleDrop}
                                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                                onDragLeave={() => setIsDragOver(false)}
                                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            >
                                <UploadIcon />
                                <span className="tw-text-sm tw-font-medium tw-text-[#172B4D]">Click to browse or drag external commend file</span>
                                <span className="tw-text-xs tw-text-[#6B778C]">Accept .jsonl file only (max 2 GB)</span>
                            </div>
                        )}
                        <input data-testid="components-uploadexternalmodeldialog-input-3"
                            ref={fileInputRef}
                            type="file"
                            accept=".jsonl,.json-l"
                            className="tw-hidden"
                            onChange={handleFileInputChange}
                        />
                        {fileTypeError && (
                            <span className="tw-text-red-500 tw-text-xs tw-mt-1 tw-block">{fileTypeError}</span>
                        )}
                    </div>

                    {/* Model Name */}
                    <TextField
                        label="Model name"
                        placeholder="Enter Model name"
                        value={modelName}
                        onChange={setModelName}
                        required
                    />

                    {/* Model Type */}
                    <div>
                        <label className="tw-text-sm tw-font-medium tw-text-[#172B4D]">
                            Model type<span className="tw-text-red-500">*</span>
                        </label>
                        <div className="tw-flex tw-items-center tw-gap-6 tw-mt-2">
                            <Radio
                                label="Co-seller"
                                value="Co-seller"
                                checked={modelType === "Co-seller"}
                                onChange={(val) => setModelType(val as ModelType)}
                                name="model-type"
                            />
                            <Radio
                                label="Substitution"
                                value="Substitution"
                                checked={modelType === "Substitution"}
                                onChange={(val) => setModelType(val as ModelType)}
                                name="model-type"
                            />
                        </div>
                    </div>

                    {/* Upload progress bar — visible only while uploading */}
                    {uploadPhase === 'uploading' && (
                        <div>
                            <div className="tw-flex tw-justify-between tw-text-xs tw-text-[#6B778C] tw-mb-1">
                                <span>Uploading file. Please do not refresh / close the page.</span>
                                <span>{uploadProgress}%</span>
                            </div>
                            <div className="tw-w-full tw-bg-[#DFE1E6] tw-rounded-full tw-h-1.5">
                                <div
                                    className="tw-bg-accent-bold tw-h-1.5 tw-rounded-full tw-transition-all tw-duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="tw-border-t tw-border-[#D1D1D6] tw-flex tw-justify-end tw-gap-2 tw-px-4 tw-py-3">
                    <Button id={`${id}-cancel-btn`} variant="secondary" onClick={handleCancel} disabled={isCreating}>
                        Cancel
                    </Button>
                    <Button
                        id={`${id}-create-btn`}
                        variant="primary"
                        disabled={!isFormValid || isCreating}
                        onClick={handleCreate}
                    >
                        {phaseLabel[uploadPhase]}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default UploadExternalModelDialog;
